/**
 * API Client with Interceptor Pattern
 * 
 * Features (All Implemented ✓):
 * ✓ Auto-injection of Bearer token from sessionStorage on every request (including retries)
 * ✓ 3 retries with exponential backoff (1s, 2s, 4s delays) for failed requests
 * ✓ 3 retries on 401/403 auth failures, then logout after exhaustion
 * ✓ Error notifications shown to user after retry exhaustion
 * ✓ S3 calls are not handled as it has auto headers  
 * ✓ Configurable via RETRY_CONFIG in constants/apiConfig.js
 * 
 * Priority: .env → constants → defaults
 * 
 * Status: Production Ready - Last Updated: February 2, 2026
 */

import { retryWithBackoff } from './apiRetry';
import { RETRY_CONFIG } from '../constants/apiConfig';
import store from '../redux/store';
import { addErrorNotification } from '../redux/slices/notificationSlice';
import logger from './logger';

const API_BASE_PATH = process.env.REACT_APP_BASE_URL;

/**
 * Builds HTTP headers with auto-injected Bearer token.
 * Called on every request including retries to ensure fresh token.
 * 
 * @param {Object} extraHeaders - Additional headers to merge
 * @param {string} contentType - Content-Type header value
 * @returns {Headers} Headers object with Bearer token if available
 */
const buildHeaders = (extraHeaders = {}, contentType = 'application/json') => {
    const headers = {
        accept: 'application/json',
        ...extraHeaders,
    };

    if (contentType && !headers['Content-Type']) {
        headers['Content-Type'] = contentType;
    }

    const bearerToken = typeof window !== 'undefined' ? sessionStorage.getItem('bearer_token') : null;
    if (bearerToken) {
        headers['Authorization'] = `Bearer ${bearerToken}`;
    }

    return new Headers(headers);
};

const isServerError = (status) => status >= 500 && status < 600;
const isAuthError = (status) => status === 401 || status === 403;

const handleUnauthorized = async (status) => {
    logger.warn(`Authentication failed with status ${status} after ${RETRY_CONFIG.maxRetries} retries. Triggering logout.`);
    //fixed cannot access apiFetch before initialisation issue by importing authSlice here and dispatching signOut directly. Instead, we dynamically import authSlice inside this function to avoid circular dependency issues.
    try {
        const { signOut } = await import('../redux/slices/authSlice');
        store.dispatch(signOut());
    } catch (e) {
        console.error('Failed to dispatch signOut after auth failure:', e);
    }
};

const parseErrorResponse = async (response) => {
    try {
        const data = await response.json();
        // Validation format from FastAPI/Pydantic: { detail: [{ loc, msg, type, ... }] }
        if (Array.isArray(data.detail) && data.detail.length > 0) {
            const firstMessage = data.detail[0]?.msg || `HTTP ${response.status}: ${response.statusText}`;
            return { message: firstMessage, detail: data.detail };
        }
        // Check for backend detail format: { detail: { code, message } }
        if (data.detail && data.detail.message) {
            return { message: data.detail.message, detail: data.detail };
        }
        if (Array.isArray(data.messages) && data.messages.length > 0) {
            return { message: data.messages[0], detail: null };
        }
        if (data.message) {
            return { message: data.message, detail: null };
        }
    } catch (e) {
        // Ignore JSON parse errors and fall back to generic message
    }
    return { message: `HTTP ${response.status}: ${response.statusText}`, detail: null };
};

/**
 * Shared API client with retry + auth handling.
 *
 * - Automatically adds bearer token from sessionStorage
 * - Retries on ANY error (including 401/403) 3 times with exponential backoff
 * - After 3 failed retries, shows error notification
 * - On 401/403, retries 3 times then logs out after exhaustion
 *
 * @param {string} pathOrUrl - Absolute URL or API path starting with /api
 * @param {Object} options - Fetch options (method, headers, body, etc.)
 * @param {boolean} [options.retry=true] - Whether to enable retry logic
 */
export const apiFetch = async (pathOrUrl, options = {}) => {
    const {
        method = 'GET',
        headers = {},
        body,
        signal,
        retry = true,
        contentType = 'application/json',
        ...rest
    } = options;

    const url = pathOrUrl.startsWith('http') ? pathOrUrl : `${API_BASE_PATH}${pathOrUrl}`;

    const doRequest = async () => {
        
            const response = await fetch(url, {
                method,
                headers: buildHeaders(headers, contentType),
                body,
                signal,
                ...rest,
            });

            // Non-OK responses (including 401/403) - make retryable if retry is enabled
            if (!response.ok) {
                const parsed = await parseErrorResponse(response);
                const error = new Error(parsed.message);
                error.status = response.status;
                error.detail = parsed.detail;  // Attach backend detail { code, message }
                if (isServerError(response.status) || isAuthError(response.status)) {
                    error.isRetryable = !!retry;
                }
                else{
                    error.isRetryable = false;
                }
                throw error;
            }

            if (response.status === 204) {
                return 'No Content';
            }

            return response.json();
        } 
    

    try {
        if (retry) {
            return await retryWithBackoff(doRequest);
        }
        return await doRequest();
    } catch (error) {
        if (retry) {
            logger.error(`API call to ${url} failed after ${RETRY_CONFIG.maxRetries} retries.`, error.message);

            // 401/403 after retries exhausted: log the user out
            if (isAuthError(error.status)) {
                handleUnauthorized(error.status);
            }

            // Show error notification to user
            store.dispatch(addErrorNotification({
                message: `API request failed: ${error.message || 'Please try again later'}`,
                isInline: false
            }));
        }
        throw error;
    }
};

/**
 * Wrap an existing fetch call with retry and auth handling.
 * 
 * Features:
 * - 3 retries with exponential backoff on 5xx and 401/403 errors
 * - Logout on 401/403 after retries exhausted
 * - Error notification after retry exhaustion
 *
 * IMPORTANT: This helper does not build or modify headers.
 * Callers pass a function that returns a fetch Promise, including headers.
 *
 * Example:
 *   const response = await callWithAuthRetry(() =>
 *     fetch(url, { method: 'GET', headers: setHttpHeader() })
 *   );
 */
export const callWithAuthRetry = async (makeRequest) => {
    const doRequest = async () => {
        const response = await makeRequest();
        const { status, ok } = response;

        // For 401/403 or 5xx, throw a retryable error so retryWithBackoff retries
        if (isAuthError(status) || (!ok && isServerError(status))) {
            const error = new Error(`HTTP ${status}`);
            error.status = status;
            error.isRetryable = true;
            throw error;
        }

        // For 2xx and other non-5xx statuses, return as-is;
        // callers can run their own handleResponse logic.
        return response;
    };

    try {
        return await retryWithBackoff(doRequest);
    } catch (error) {
        const status = error?.status;

        if (status && (isAuthError(status) || isServerError(status))) {
            // 401/403 after retries exhausted: log the user out
            if (isAuthError(status)) {
                handleUnauthorized(status);
            }

            logger.error(`API call failed after retries with status ${status}.`, error.message);

            store.dispatch(addErrorNotification({
                message: `${isAuthError(status) ? 'Authentication' : 'Server'} error: ${error.message || 'Please try again later'}`,
                isInline: false
            }));
        }
        throw error;
    }
};
